// @ts-expect-error module not typed
import EmailAddressRegex from 'PatternValidationJS/regex/EmailAddressRegex';
// @ts-expect-error module not typed
import UrlRegexFactory from 'PatternValidationJS/regex/factories/UrlRegexFactory';
import { Fragment } from 'hubspot-prosemirror/pm/model';
import { ensureUrlHasProtocol } from '../../../draft-transplant/utils/libUtils';
const emailSource = EmailAddressRegex.source.replace(/^\^(.*)\$$/, '$1');
const mailtoRegex = `mailto:${emailSource}`;
const urlSourceWithProtocol = UrlRegexFactory({
  protocolRequired: true
}).source.replace(/^\^(.*)\$$/, '$1');

// Known limitation: matches any token containing a `.`, so file names
// (`readme.txt`) and typos (`about.And`) will also be linkified. A canonical
// fix would require a TLD allowlist; we accept this limitation, matching
// DraftJS behavior.
const urlSourceBareAllowed = UrlRegexFactory({
  protocolRequired: false
}).source.replace(/^\^(.*)\$$/, '$1');
const emailFullMatchRegex = new RegExp(`^(?:${emailSource})$`);
function getUrlSource(autoDetectBareUrls) {
  return autoDetectBareUrls ? urlSourceBareAllowed : urlSourceWithProtocol;
}
function buildUrlRegex(autoDetectBareUrls) {
  return new RegExp(`\\b(${getUrlSource(autoDetectBareUrls)})|(${mailtoRegex})\\b`, 'g');
}
const VALID_DOMAIN_LABEL = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const HAS_PROTOCOL = /^(?:https?:)?\/\//;
const TRAILING_PUNCTUATION = /[?.,]+$/;
function hasValidDomainLabels(urlMatch) {
  const withoutProtocol = urlMatch.replace(HAS_PROTOCOL, '');
  const domainWithPort = withoutProtocol.split(/[/?#]/)[0];
  const domain = domainWithPort.replace(/:\d+$/, '');
  const labels = domain.split('.');
  return labels.every(label => VALID_DOMAIN_LABEL.test(label));
}
function isBareUrlWithInvalidDomain(text) {
  const cleaned = text.replace(TRAILING_PUNCTUATION, '');
  return !HAS_PROTOCOL.test(cleaned) && !hasValidDomainLabels(cleaned);
}
function getEmailRanges(text) {
  const emailRegex = new RegExp(emailSource, 'g');
  const ranges = [];
  let match = emailRegex.exec(text);
  while (match) {
    ranges.push([match.index, match.index + match[0].length]);
    match = emailRegex.exec(text);
  }
  return ranges;
}
function isInsideEmail(start, end, emailRanges) {
  return emailRanges.some(([emailStart, emailEnd]) => start < emailEnd && end > emailStart);
}
export function isUrl(text, options = {}) {
  const autoDetectBareUrls = Boolean(options.autoDetectBareUrls);
  if (!buildUrlRegex(autoDetectBareUrls).test(text)) return false;
  if (autoDetectBareUrls && isBareUrlWithInvalidDomain(text)) return false;
  return true;
}
export function isUrlAndNotEmail(text, options = {}) {
  if (emailFullMatchRegex.test(text)) return false;
  return isUrl(text, options);
}
export function linkifyTextNode(node, options = {}) {
  var _node$text3;
  const autoDetectBareUrls = Boolean(options.autoDetectBareUrls);
  const linkified = [];
  const urlRegex = buildUrlRegex(autoDetectBareUrls);
  const schema = node.type.schema;
  const emailRanges = autoDetectBareUrls && node.text ? getEmailRanges(node.text) : [];
  let pos = 0;
  let matchedUrlText = node.text && urlRegex.exec(node.text);
  while (matchedUrlText) {
    var _node$text, _node$text2;
    const start = matchedUrlText.index;
    const end = start + matchedUrlText[0].length;
    if (autoDetectBareUrls && isInsideEmail(start, end, emailRanges)) {
      matchedUrlText = node.text && urlRegex.exec(node.text);
      continue;
    }
    if (autoDetectBareUrls && isBareUrlWithInvalidDomain(matchedUrlText[0])) {
      matchedUrlText = node.text && urlRegex.exec(node.text);
      continue;
    }
    const link = schema.marks.link;
    const initialText = start > 0 && ((_node$text = node.text) === null || _node$text === void 0 ? void 0 : _node$text.slice(pos, start));
    if (initialText) {
      linkified.push(schema.text(initialText, node.marks));
    }
    const urlText = (_node$text2 = node.text) === null || _node$text2 === void 0 ? void 0 : _node$text2.slice(start, end);
    if (urlText) {
      var _punctuationMatch$gro, _punctuationMatch$gro2;
      const punctuationMatch = urlText.match(/(?<url>.*)(?<endingPunctuation>[?.,]+)$/);
      const url = (punctuationMatch === null || punctuationMatch === void 0 || (_punctuationMatch$gro = punctuationMatch.groups) === null || _punctuationMatch$gro === void 0 ? void 0 : _punctuationMatch$gro.url) || urlText;
      const endingPunctuation = punctuationMatch === null || punctuationMatch === void 0 || (_punctuationMatch$gro2 = punctuationMatch.groups) === null || _punctuationMatch$gro2 === void 0 ? void 0 : _punctuationMatch$gro2.endingPunctuation;
      const href = autoDetectBareUrls ? ensureUrlHasProtocol(url) : url;
      linkified.push(schema.text(url, link.create({
        href
      }).addToSet(node.marks)));
      if (endingPunctuation) {
        linkified.push(schema.text(endingPunctuation, node.marks));
      }
    }
    pos = end;
    matchedUrlText = node.text && urlRegex.exec(node.text);
  }
  const finalText = (_node$text3 = node.text) === null || _node$text3 === void 0 ? void 0 : _node$text3.slice(pos);
  if (finalText) {
    linkified.push(schema.text(finalText, node.marks));
  }
  return linkified;
}
export function linkify(fragment, options = {}) {
  const linkified = [];
  for (let i = 0; i < fragment.childCount; i++) {
    const child = fragment.child(i);
    if (child.isText) {
      const isAlreadyLinked = child.marks.some(mark => mark.type.name === 'link');
      if (isAlreadyLinked) {
        linkified.push(child);
      } else {
        linkified.push(...linkifyTextNode(child, options));
      }
    } else {
      linkified.push(child.copy(linkify(child.content, options)));
    }
  }
  return Fragment.fromArray(linkified);
}